import type { PubSub } from '../../PubSub';
import type { GameEvents } from '../../events';

/**
 * Blocks the script until the player acknowledges the prompt.
 *
 * The expected key is accepted two ways: as a single keystroke via `console:key`
 * (no Enter needed — these prompts are one character long, so requiring a return
 * press is pure friction), or as a submitted line via `console:input-submit`, which
 * keeps Enter working and covers virtual keyboards that don't report usable
 * `key` values.
 *
 * Anything else re-prints the prompt on a fresh line, the way a shell re-offers a
 * rejected prompt, and discards the refused keystroke so the caret starts clean.
 */
export class ForceAcknowledge {
    /** Seconds before the prompt gives up and dismisses itself. */
    private static readonly TIMEOUT_MS = 30_000;

    constructor(private prompt: string, private expected: string) {}

    execute(pubsub: PubSub<GameEvents>, flag: { cancelled: boolean }): Promise<void> {
        return new Promise(async (resolve) => {
            if (flag.cancelled) { resolve(); return; }

            await pubsub.publish('console:type-line', this.prompt);
            if (flag.cancelled) { resolve(); return; }

            await pubsub.publish('console:show-input');

            const expectedKey = this.expected.trim().toLowerCase();
            let resolved = false;
            let reprinting = false;
            let timeout: ReturnType<typeof setTimeout>;

            const done = () => {
                if (resolved) return;
                resolved = true;
                unsubKey();
                unsubInput();
                unsubCancel();
                clearTimeout(timeout);
                resolve();
            };

            const accept = () => {
                pubsub.publish('console:hide-input');
                done();
            };

            /**
             * Re-offers the prompt after a refused keystroke. The `reprinting` guard
             * swallows keys pressed while the new line is still typing, so holding a
             * key down can't stack up overlapping prompts. The input is cleared only
             * once that typing is done: the refused character is inserted *after* the
             * keydown handler returns, so clearing any earlier would miss it.
             */
            const reprint = async () => {
                if (reprinting || resolved) return;
                reprinting = true;

                await pubsub.publish('console:type-line', this.prompt);
                if (!resolved && !flag.cancelled) {
                    await pubsub.publish('console:clear-input');
                    await pubsub.publish('console:show-input');
                }

                reprinting = false;
            };

            const unsubKey = pubsub.subscribe('console:key', (key: string) => {
                if (flag.cancelled) { done(); return; }
                if (key.trim().toLowerCase() !== expectedKey) { reprint(); return; }
                accept();
            });

            const unsubInput = pubsub.subscribe('console:input-submit', (value: string) => {
                if (flag.cancelled) { done(); return; }
                if (value.trim().toLowerCase() !== expectedKey) { reprint(); return; }
                accept();
            });

            // Resolves if the console is dismissed externally (flag.cancelled path)
            const unsubCancel = pubsub.subscribe('console:hide-input', () => done());

            timeout = setTimeout(() => {
                pubsub.publish('console:hide-input');
                done();
            }, ForceAcknowledge.TIMEOUT_MS);
        });
    }
}
